import { useState, useEffect, useRef } from "react";
import {
  Send,
  Settings2,
  MessageSquare,
  BookOpen,
  AlertCircle,
  Check,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import type {
  Session,
  ModelDocument,
  Operation,
} from "../../shared/contracts.js";
import { api } from "../api.js";
import { findHelp } from "../../shared/help.js";
import "./panels.css";
interface Message {
  id: string;
  role: "user" | "assistant";
  text: string;
  sources?: any[];
  operations?: Operation[];
  provider?: string;
  applied?: boolean;
  error?: boolean;
}
export default function AssistantPanel({
  session,
  document: model,
  onOperations,
}: {
  session: Session;
  document?: ModelDocument;
  onOperations?: (ops: Operation[], summary: string) => void;
}) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState<any>({
    mode: "local",
    model: "",
    endpoint: "",
  });
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const canAdmin = session.roles.some((r) =>
    ["tenant-super-admin", "studio-admin", "platform-super-admin"].includes(r),
  );
  useEffect(() => {
    api("/assistant/config")
      .then((c) => setConfig(c))
      .catch((e) => setError(e.message));
  }, []);
  useEffect(
    () => end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
    [messages, busy],
  );
  async function send(text = input) {
    if (!text.trim() || busy) return;
    setInput("");
    setBusy(true);
    setError("");
    const next: Message = { id: crypto.randomUUID(), role: "user", text };
    setMessages((m) => [...m, next]);
    try {
      const res = await api("/assistant/chat", {
        method: "POST",
        body: JSON.stringify({
          message: text,
          modelId: model?.model.modelId,
          history: messages
            .slice(-8)
            .map((m) => ({ role: m.role, content: m.text })),
        }),
      });
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          text: res.answer || res.message || "No answer was returned.",
          sources: res.sources || [],
          operations: res.operations || res.proposal?.operations,
          provider: res.provider,
        },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          text: (e as Error).message,
          error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  }
  async function saveConfig() {
    setBusy(true);
    try {
      const res = await api("/assistant/config", {
        method: "PUT",
        body: JSON.stringify(config),
      });
      setConfig(res);
      setEditing(false);
      setError("");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const article = source ? findHelp(source) : null;
  return (
    <div className="dgx-assistant" data-testid="assistant-panel">
      <div className="dgx-panel-heading">
        <span className="dgx-eyebrow">
          <MessageSquare size={15} /> MODELING ASSISTANT
        </span>
        {canAdmin && (
          <button
            title="Assistant configuration"
            aria-label="Assistant configuration"
            className="dgx-icon-button"
            onClick={() => setEditing(!editing)}
          >
            <Settings2 size={17} />
          </button>
        )}
      </div>
      <div className="dgx-provider">
        <span
          className={`dgx-status-dot ${config.mode === "off" ? "off" : ""}`}
        />
        {config.mode === "off"
          ? "Assistant off"
          : config.mode === "public"
            ? "Approved public provider"
            : "Local provider"}
        <span>{config.model || "Configure a model"}</span>
      </div>
      {editing && (
        <form
          className="dgx-form dgx-callout"
          onSubmit={(e) => {
            e.preventDefault();
            void saveConfig();
          }}
        >
          <h3>Inference policy</h3>
          <label>
            Mode
            <select
              value={config.mode || "local"}
              onChange={(e) => setConfig({ ...config, mode: e.target.value })}
            >
              <option value="local">Local</option>
              <option value="public">Public</option>
              <option value="off">Off</option>
            </select>
          </label>
          <label>
            Model
            <input
              value={config.model || ""}
              onChange={(e) => setConfig({ ...config, model: e.target.value })}
              placeholder="Installed model identifier"
            />
          </label>
          <label>
            Approved endpoint
            <input
              value={config.endpoint || ""}
              onChange={(e) =>
                setConfig({ ...config, endpoint: e.target.value })
              }
              placeholder="http://ollama:11434"
            />
          </label>
          <p className="dgx-muted">
            The server enforces approved destinations and delegated
            configuration rights. Local failure never switches to Public.
          </p>
          <button className="dgx-primary" disabled={busy}>
            Save configuration
          </button>
        </form>
      )}
      {error && (
        <p className="dgx-error" role="alert">
          <AlertCircle size={15} />
          {error}
        </p>
      )}
      <div className="dgx-chat" aria-live="polite">
        {!messages.length && (
          <div className="dgx-chat-empty">
            <div className="dgx-chat-symbol">
              <MessageSquare size={28} />
            </div>
            <h2>Work through the details.</h2>
            <p>
              Ask about a DataGlow feature, a validation issue or the next step
              in your model.
            </p>
            {[
              "How do I preserve business keys?",
              "Explain manual save and automatic receive",
              "How do I submit a model through the API?",
            ].map((t) => (
              <button
                key={t}
                onClick={() => void send(t)}
                disabled={busy || config.mode === "off"}
              >
                {t}
                <Send size={13} />
              </button>
            ))}
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`dgx-message ${m.role} ${m.error ? "error" : ""}`}
          >
            <span className="dgx-message-label">
              {m.role === "user" ? session.name : "Assistant"}
              {m.provider && ` · ${m.provider}`}
            </span>
            <p>{m.text}</p>
            {m.sources?.length ? (
              <div className="dgx-source-list">
                {m.sources.map((s, i) => (
                  <button
                    key={i}
                    onClick={() =>
                      setSource(typeof s === "string" ? s : s.id || s.topicId)
                    }
                  >
                    <BookOpen size={12} />
                    {typeof s === "string"
                      ? findHelp(s).title
                      : s.title || s.id || "Product guide"}
                  </button>
                ))}
              </div>
            ) : null}
            {m.operations?.length && (
              <div className="dgx-proposal">
                <strong>{m.operations.length} proposed operation(s)</strong>
                <pre>{JSON.stringify(m.operations, null, 2)}</pre>
                <button
                  className="dgx-primary"
                  disabled={m.applied || !onOperations}
                  onClick={() => {
                    if (onOperations) {
                      onOperations(
                        m.operations!,
                        "Reviewed assistant proposal",
                      );
                      setMessages((prev) =>
                        prev.map((x) =>
                          x.id === m.id ? { ...x, applied: true } : x,
                        ),
                      );
                    }
                  }}
                >
                  {m.applied ? (
                    <>
                      <Check size={14} />
                      Sent for validation
                    </>
                  ) : (
                    "Review and submit proposal"
                  )}
                </button>
                <small>
                  Current permissions, validation and revision checks still
                  apply.
                </small>
              </div>
            )}
          </div>
        ))}
        {busy && !editing && (
          <div className="dgx-thinking">
            <Loader2 size={15} className="dgx-spin" />
            Waiting for the configured provider...
          </div>
        )}
        <div ref={end} />
      </div>
      {article && (
        <details className="dgx-details" open>
          <summary>
            {article.title}
            <button
              type="button"
              className="dgx-link"
              onClick={(e) => {
                e.preventDefault();
                setSource(null);
              }}
            >
              Close
            </button>
          </summary>
          <p>{article.summary}</p>
          <ol>
            {article.steps.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ol>
        </details>
      )}
      <form
        className="dgx-chat-input"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <textarea
          aria-label="Message the assistant"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            config.mode === "off"
              ? "Assistant is disabled. Product Help remains available."
              : "Ask about this workspace..."
          }
          disabled={config.mode === "off"}
          rows={3}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button
          className="dgx-primary"
          aria-label="Send message"
          disabled={!input.trim() || busy || config.mode === "off"}
        >
          <Send size={16} />
        </button>
      </form>
      <p className="dgx-footnote">
        <ShieldCheck size={13} />
        Model changes require review. No automatic public fallback.
      </p>
    </div>
  );
}
